import { createAsyncThunk } from '@reduxjs/toolkit';
import { GetHabitPackPayload, UpdateApprovalStatusProps } from 'interfaces';
import focusBearApi from 'services/axios-config';
import Endpoints from 'constants/endpoints';
import { changeIsSettingLoaded, updateError } from '../setting/slice';
import { getUserSettings } from '../setting/extra';
import { toast } from 'react-toastify';
import { EMPTY_STRING, HTTP_STATS_CODE } from 'constants/general';
import { BASE_URL } from 'constants/routes';
import { REQUEST_ERROR_MESSAGE_KEY } from 'constants/error.constant';
import { toastRequestError } from 'utils/api-error.util';
import {
  setCurrentViewedPack,
  setFeaturedHabitPacks,
  setMarketplaceApprovedPacks,
  setUserCreatedHabitPacks,
  updateAreHabitPacksLoading,
  updateInstallingPack,
  updateIsCurrentHabitPackFetching,
  updateIsHabitPacksDeleting,
  updateIsSharedHabitPackFetching,
  updateIsUserInstalledPacksFetching,
  updateIsUserUpsertingPack,
  updateOnboardingHabitPacks,
  updateSharedViewedPack,
  updateUninstallingPack
} from './slice';
import { updateIsInquiryModalShown } from '../inquiry/slice';
import { AxiosError } from 'axios';
import { HabitPackType } from 'interfaces/common.interface';
import { SettingsType } from 'interfaces/setting.interface';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import { hasMarketplaceAuthSession } from 'utils/marketplace.util';
import { asArray } from 'utils/array.util';
import { getApiLanguageCode } from 'utils/helpers';

export const getHabitPacks = createAsyncThunk(
  'habit_packs/get_habit_packs',
  async (params: GetHabitPackPayload, { dispatch }) => {
    try {
      const { searchTerm = EMPTY_STRING, language, ...rest } = params;
      dispatch(updateAreHabitPacksLoading(true));
      const { data } = await focusBearApi.get(Endpoints.HABIT_PACKS, {
        params: {
          ...rest,
          ...(language !== undefined
            ? { language: getApiLanguageCode(language) }
            : {})
        }
      });
      const habitPacks = asArray<HabitPackType>(data);
      // Each flag populates its own slice independently. The settings and
      // webview habit-edit flows request `is_featured` and
      // `marketplace_approval_status` together, so an else-if chain here would
      // leave `marketplaceApprovedPacks` empty (no habit-pack suggestions).
      if (params.is_featured) {
        const featuredPacks = habitPacks.filter(
          (pack: HabitPackType) => pack.is_featured
        );
        dispatch(setFeaturedHabitPacks(featuredPacks));
      }
      if (params.marketplace_approval_status) {
        const marketApprovedPacks = habitPacks.filter(
          (pack: HabitPackType) =>
            pack.marketplace_approval_status &&
            (pack.pack_name
              ?.toLowerCase()
              .includes(searchTerm?.toLowerCase()) ||
              pack.creator_name
                ?.toLowerCase()
                .includes(searchTerm?.toLowerCase()))
        );

        dispatch(setMarketplaceApprovedPacks(marketApprovedPacks));
      }
      if (params.user_id) {
        dispatch(setUserCreatedHabitPacks(habitPacks));
      }
      if (params.featured_for_onboarding) {
        dispatch(updateOnboardingHabitPacks(habitPacks));
      }
      return habitPacks;
    } catch (error) {
      captureException(error);
    } finally {
      dispatch(updateAreHabitPacksLoading(false));
    }
  }
);

export const getUserInstalledHabitPacks = createAsyncThunk(
  'habit_packs/get_user_installed_habit_packs',
  async (showInquiryModal: boolean, { dispatch }) => {
    try {
      const { data } = await focusBearApi.get<HabitPackType[]>(
        Endpoints.USER_INSTALLED_HABIT_PACKS
      );
      if (showInquiryModal) {
        dispatch(updateIsInquiryModalShown(true));
      }
      return asArray<HabitPackType>(data);
    } catch (error) {
      captureException(error);
    }
  }
);

export const updatePackMarketPlaceRequestStatus = createAsyncThunk(
  'habit_packs/update_marketplace_approval_status',
  async ({ habitPack, status }: UpdateApprovalStatusProps, { dispatch }) => {
    try {
      const modifiedHabitPack: HabitPackType = {
        ...habitPack,
        marketplace_request: status
      };
      dispatch(setCurrentViewedPack(modifiedHabitPack));
      dispatch(upsertHabitPack(modifiedHabitPack));
    } catch (error) {
      toast.error(t('market.could_not_update_habit_pack'));
      captureException(error);
    }
  }
);

export const removePackFromMarketplace = createAsyncThunk(
  'habit_packs/remove_pack_from_marketplace',
  async (habitPack: HabitPackType, { dispatch }) => {
    try {
      const modifiedHabitPack: HabitPackType = {
        ...habitPack,
        marketplace_approval_status: false
      };
      dispatch(setCurrentViewedPack(modifiedHabitPack));
      dispatch(upsertHabitPack(modifiedHabitPack));
      dispatch(getHabitPacks({ user_id: habitPack.user_id }));
    } catch (error) {
      toast.error(t('market.could_not_update_habit_pack'));
      captureException(error);
    }
  }
);

export const deleteHabitPack = createAsyncThunk(
  'habit_packs/delete_habit_pack',
  async (
    { packId, user_id }: { packId: string; user_id: string },
    { dispatch }
  ) => {
    try {
      dispatch(updateIsHabitPacksDeleting(true));
      await focusBearApi.delete(
        Endpoints.ACCESS_HABIT_PACK_BY_ID.replace('{pack_id}', packId)
      );
      dispatch(updateIsHabitPacksDeleting(false));
      Promise.allSettled([
        dispatch(getHabitPacks({ user_id })),
        dispatch(getUserSettings())
      ]);
    } catch (error) {
      dispatch(updateIsHabitPacksDeleting(false));
      captureException(error);
      toast.warning(t('market.could_not_delete_habit_pack'), {
        position: 'bottom-left',
        autoClose: 3000
      });
    }
  }
);

export const upsertHabitPack = createAsyncThunk(
  'habit_packs/upsert_habit_pack',
  async (habitPack: SettingsType, { dispatch, rejectWithValue }) => {
    try {
      dispatch(updateIsUserUpsertingPack(true));
      const { status, data } = await focusBearApi.put(
        Endpoints.HABIT_PACKS,
        habitPack
      );
      dispatch(updateIsUserUpsertingPack(false));
      if (status === HTTP_STATS_CODE.SUCCESS) {
        return { success: true as const };
      }
      toast.warning(t('market.could_not_update_habit_pack'));
      captureException({ status, data });
      return rejectWithValue(null);
    } catch (error) {
      dispatch(updateIsUserUpsertingPack(false));
      toast.error(t('market.could_not_update_habit_pack'));
      captureException(error);
      return rejectWithValue(null);
    }
  }
);

export const installHabitPack = createAsyncThunk(
  'habit_packs/install_habit_pack',
  async (packId: string, { dispatch }) => {
    try {
      dispatch(updateInstallingPack(true));
      const response = await focusBearApi.post(
        Endpoints.INSTALL_HABIT_PACK.replace('{pack_id}', packId)
      );
      if (response.status === HTTP_STATS_CODE.CREATED) {
        dispatch(getUserSettings());
        dispatch(getUserInstalledHabitPacks(true));
      } else {
        toast.warning(t('market.habit_pack_install_fail'));
      }
    } catch (error) {
      toast.warning(t('market.habit_pack_install_fail'));
      captureException(error);
    } finally {
      dispatch(updateInstallingPack(false));
    }
  }
);

export const installSharedHabitPack = createAsyncThunk(
  'habit_packs/install_habit_pack_as_default_settings',
  async (packId: string, { dispatch }) => {
    try {
      dispatch(updateInstallingPack(true));
      const { status } = await focusBearApi.get(
        Endpoints.INSTALL_HABIT_PACK_AS_DEFAULT_SETTINGS.replace(
          '{pack_id}',
          packId
        )
      );
      if (status !== HTTP_STATS_CODE.SUCCESS) {
        toast.warning(t('market.habit_pack_install_fail'));
      }
      dispatch(updateIsInquiryModalShown(true));
      dispatch(changeIsSettingLoaded(false));
      dispatch(updateInstallingPack(false));
    } catch (error) {
      dispatch(updateInstallingPack(false));
      toast.warning(t('market.habit_pack_install_fail'));
      captureException(error);
    }
  }
);

export const uninstallHabitPack = createAsyncThunk(
  'habit_packs/uninstall_habit_pack',
  async (packId: string, { dispatch }) => {
    try {
      dispatch(updateUninstallingPack(true));
      const response = await focusBearApi.delete(
        Endpoints.UNINSTALL_HABIT_PACK.replace('{pack_id}', packId)
      );
      if (response.status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(changeIsSettingLoaded(false));
        dispatch(getUserInstalledHabitPacks(false));
      } else {
        toast.warning(t('market.habit_pack_install_fail'));
      }
      dispatch(updateUninstallingPack(false));
    } catch (error) {
      captureException(error);
      toast.warning(t('market.habit_pack_uninstall_fail'));
      dispatch(updateUninstallingPack(false));
    }
  }
);

export const getHabitPackById = createAsyncThunk(
  'habit_packs/get_habit_pack_by_id',
  async (packId: string, { dispatch }) => {
    try {
      dispatch(updateIsCurrentHabitPackFetching(true));
      const { data } = await focusBearApi.get(
        Endpoints.ACCESS_HABIT_PACK_BY_ID.replace('{pack_id}', packId)
      );
      if (hasMarketplaceAuthSession()) {
        dispatch(updateIsUserInstalledPacksFetching(true));
        dispatch(getUserInstalledHabitPacks(false));
      } else {
        dispatch(updateIsUserInstalledPacksFetching(false));
      }
      return data ?? null;
    } catch (error) {
      toast.error(t('market.could_not_fetch_habit_pack'));
      captureException(error);
    }
  }
);

export const getSharedHabitPackById = createAsyncThunk(
  'habit_packs/get_shared_habit_pack_by_id',
  async (packId: string, { dispatch }) => {
    try {
      dispatch(updateIsSharedHabitPackFetching(true));
      const response = await fetch(
        `${BASE_URL}${Endpoints.ACCESS_HABIT_PACK_BY_ID.replace('{pack_id}', packId)}`
      );
      const data = await response.json();
      if (data && data?.statusCode !== 404) {
        dispatch(updateSharedViewedPack(data));
      }
      dispatch(updateIsSharedHabitPackFetching(false));
    } catch (error) {
      dispatch(updateIsSharedHabitPackFetching(false));
      dispatch(
        updateError({
          message: 'market.could_not_fetch_habit_pack',
          status: (error as AxiosError).response?.status
        })
      );
      captureException(error);
    }
  }
);

// TODO: This implementation appears incorrect and needs to be fixed.
export const installHabit = createAsyncThunk<string, string>(
  'habit_packs/install_habit',
  async (activityId, { dispatch, rejectWithValue }) => {
    try {
      const refreshPromise = dispatch(getUserSettings()).unwrap();

      const [refreshRes] = await Promise.allSettled([refreshPromise]);
      if (refreshRes.status === 'fulfilled') {
        return activityId;
      } else {
        toast.error(t('Settings refresh failed'));
        return rejectWithValue(activityId);
      }
    } catch (err) {
      toastRequestError(err, {
        fallbackKey: REQUEST_ERROR_MESSAGE_KEY.HABIT_INSTALL_FAILED
      });
      return rejectWithValue(activityId);
    }
  }
);
